import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { CaseStrip } from "@/components/CasePreview";
import { cities, type CityData } from "@/lib/cities";

const services = [
  {
    title: "Shop-Erstellung",
    text: "Aufbau Ihres Shopify-Shops von der Struktur über das Theme bis zu Produkten, Versand und Zahlarten. Sie bekommen einen Shop, den Ihr Team selbst pflegen kann.",
  },
  {
    title: "Anpassungen und Entwicklung",
    text: "Theme- und Liquid-Entwicklung, eigene Sections, Schnittstellen, Datenimporte und Fehlerbehebung. Abgerechnet nach Aufwand: 110 € netto pro Stunde, bei größeren Umbauten mit Festpreis.",
  },
  {
    title: "Conversion-Optimierung",
    text: "Ihr Shop hat Besucher, aber zu wenige Bestellungen? Ich finde die Stellen, an denen Käufer abspringen, und baue sie um. Pakete starten bei 3.990 € netto.",
  },
];

const faqs = (c: CityData) => [
  {
    q: `Arbeiten Sie auch für Kunden in ${c.name}?`,
    a: `Ja. Ich betreue Kunden in ${c.name} und ganz ${c.region} vollständig remote. Abstimmung per E-Mail oder Call, Ergebnisse sehen Sie immer zuerst in einer Testumgebung.`,
  },
  {
    q: "Was kostet ein Shopify Freelancer?",
    a: "Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab. Conversion-Optimierung startet bei 3.990 € netto. Vor dem Start bekommen Sie eine klare Aufwandsschätzung.",
  },
  {
    q: "Wie schnell kann es losgehen?",
    a: "Nach Ihrer Anfrage melde ich mich innerhalb von 48 Stunden mit einer Einschätzung. Der Starttermin hängt von meiner aktuellen Auslastung ab, das erfahren Sie transparent im ersten Gespräch.",
  },
  {
    q: "Übernehmen Sie auch bestehende Shops?",
    a: "Ja, das ist der häufigste Fall. Ich sehe mir Ihren Shop an und sage Ihnen, welche Anpassungen sich lohnen und welche nicht.",
  },
];

export function CityPage({ city }: { city: CityData }) {
  const others = cities.filter((c) => c.slug !== city.slug);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section id="anfrage" className="scroll-mt-20 border-b border-border/60 bg-gradient-to-b from-primary/10 via-background to-background">
          <div className="mx-auto max-w-4xl px-5 py-16 sm:py-20">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">
              Shopify Freelancer in {city.name}
            </p>
            <h1 className="mt-3 text-3xl font-bold leading-tight sm:text-4xl">
              Shopify Freelancer für {city.name}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{city.intro}</p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Wenn Ihr Shop Zeit frisst, technisch festhängt oder zu wenig verkauft, bekommen Sie
              einen klaren nächsten Schritt statt einer langen Verkaufsschleife.
            </p>
            <div className="mt-8 max-w-xl">
              <LeadForm
                source={`stadt-${city.slug}`}
                headline="Shop kurz einschätzen lassen"
                messagePlaceholder="Was soll an Ihrem Shop besser werden?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-14">
          <div className="mx-auto max-w-4xl px-5">
            <h2 className="text-2xl font-bold">In drei Schritten zur Lösung</h2>
            <div className="mt-7 grid gap-5 sm:grid-cols-3">
              {[
                ["01", "Sie schildern die Aufgabe", "Eine kurze Nachricht und, falls vorhanden, Ihre Shop-Adresse reichen."],
                ["02", "Sie erhalten Klarheit", "Innerhalb von 48 Stunden bekommen Sie Einschätzung, nächsten Schritt und groben Aufwand."],
                ["03", "Ich setze sauber um", "Gearbeitet wird in einer Kopie. Live geht die Änderung erst nach Ihrer Freigabe."],
              ].map(([no, title, text]) => (
                <div key={no} className="border-l border-primary/50 pl-4">
                  <span className="font-mono text-[11px] text-primary">{no}</span>
                  <h3 className="mt-2 font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-14">
          <h2 className="text-2xl font-bold">Leistungen für Ihren Shop in {city.name}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            {services.map((s) => (
              <div key={s.title} className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="font-semibold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 leading-relaxed text-muted-foreground">{city.local}</p>
        </section>

        <section className="border-t border-border/60 bg-card/40">
          <div className="mx-auto max-w-4xl px-5 py-14">
            <h2 className="text-2xl font-bold">Ausgewählte Projekte</h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Einige der Shops, an denen ich gearbeitet habe, inklusive der Messwerte vor und nach der Umsetzung.
            </p>
            <div className="mt-8">
              <CaseStrip slugs={["kremka", "deinlunu", "gordigear"]} />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-14">
          <h2 className="text-2xl font-bold">Häufige Fragen</h2>
          <div className="mt-6 space-y-4">
            {faqs(city).map((f) => (
              <details key={f.q} className="rounded-xl border border-border/60 bg-card p-5">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 pb-16">
          <h2 className="text-xl font-bold">Weitere Standorte</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {others.map((c) => (
              <Link
                key={c.slug}
                to="/shopify-freelancer/$city"
                params={{ city: c.slug }}
                className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
              >
                {c.name} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
      <StickyCta targetId="anfrage" />
    </div>
  );
}
